<template>
    <sidebar @close="$emit('close')">
        <template v-slot:title v-if="deduction">
            {{ deduction.name }}
        </template>

        <template v-slot:header v-if="deduction">
            <div class="row mb-0">
                <div class="col">
                    <span class="text-muted">
                        {{ deduction.company.name }}
                    </span>
                </div>
            </div>
            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabDeductionContentMain" id="tabDeductionLinkMain" data-toggle="tab" role="tab" aria-controls="tabDeductionContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content" v-if="deduction">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabDeductionContentMain" role="tabpanel" aria-labelledby="tabDeductionLinkMain">
                    <!-- Deduction type details widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Deduction Type</template>

                        <template v-slot:headerbutton>
                            <button v-if="isCustomizable" type="button" class="btn btn-outline-primary" @click.prevent="editDeductionType">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                            <button v-if="deduction.is_active" type="button" class="btn btn-outline-primary ml-1" @click.prevent="deactivate()">
                                Deactivate
                            </button>
                            <button v-else type="button" class="btn btn-outline-primary ml-1" @click.prevent="reactivate()">
                                Reactivate
                            </button>
                        </template>

                        <slot>
                            <div class="p-2">
                                <div>
                                    <span class="text-semibold">Name: </span>
                                    <span>{{ deduction.name }}</span>
                                </div>
                                <div>
                                    <span class="text-semibold">Base Deduction Type: </span>
                                    <span>{{ deduction.deduction_type.name }}</span>
                                </div>
                                <div>
                                    <span class="text-semibold">For Primary Benefit of Employer: </span>
                                    <span>{{ deduction.for_primary_benefit_of_employer ? 'Yes' : 'No' }}</span>
                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Positions widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Positions ({{ deduction.num_positions }})</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editPositions">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div class="info-widget-empty p-2" v-if="deduction.default_for_all_positions">
                                Available to all positions at this company.
                            </div>
                            <div v-else-if="activePositionRels.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Position</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="rel in activePositionRels" :key="rel.id">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ rel.position.terminal_value }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This deduction is not associated with any positions.
                            </div>
                        </slot>
                    </info-widget>
                </div>
            </div>
        </slot>

        <edit-deduction-positions-modal v-if="deduction" ref="editDeductionPositionsModal" :deduction="deduction" :client="client" @updated="onUpdate" @close="editPositionsModalOpen = false" />
        <edit-deduction-type-modal v-if="deduction && editDeductionTypeModalOpen" :deduction="deduction" :client="client" @updated="onUpdate" @close="editDeductionTypeModalOpen = false" />
    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import EditDeductionPositionsModal from './EditDeductionPositionsModal'
import EditDeductionTypeModal from './EditDeductionTypeModal'

export default {
    props: ['deductionId', 'client', 'tab', ],
    components: {Sidebar, InfoWidget, ButtonRow, EditDeductionPositionsModal, EditDeductionTypeModal},
    computed: {
        activePositionRels() {
            let ret = []
            if (this.deduction?.position_rels) {
                ret = this.deduction.position_rels.filter(r => r.position.is_active)
            }
            return ret
        },
        isCustomizable() {
            return (this.deduction?.deduction_type?.fields_to_customize?.length || 0) > 0
        }
    },
    data() {
        return {
            deduction: null,
            activeTab: this.tab || 'main',
            editPositionsModalOpen: false,
            editDeductionTypeModalOpen: false,
        }
    },
    watch: {
        deductionId() {
            this.getDeduction()
        }
    },
    mounted() {
        this.getDeduction()
    },
    methods: {
        getDeduction() {
            this.deduction = null
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/deduction-types/${this.deductionId}`).then(resp => {
                this.deduction = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        editPositions() {
            this.editPositionsModalOpen = true
            this.$refs.editDeductionPositionsModal.open()
        },
        editDeductionType() {
            this.editDeductionTypeModalOpen = true
        },
        deactivate() {
            if (!confirm('Are you sure you want to deactivate this deduction?')) {return}
            this.setStatus('inactive')
        },
        reactivate() {
            if (!confirm('Are you sure you want to reactivate this deduction?')) {return}
            this.setStatus('active')
        },
        setStatus(status) {
            this.$store.dispatch('START_LOADING')
            this.$api.patch(`/clients/${this.client.id}/settings/deduction-types/${this.deduction.id}`, {status}).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.onUpdate(resp)
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        onUpdate(deduction) {
            if (deduction) {
                Object.assign(this.deduction, deduction)
            }
            this.$emit('updated')
        }
    },
}
</script>
